<script setup lang="ts">
import { onMounted, ref, watch, watchEffect, computed } from 'vue'

import BtnImprint from '@/components/BtnImprint.vue'
import ListItem from '@/components/ListItem.vue'

import config, { fondOfKidsStore } from '@/config'
import calculateDistance from '@/lib/calculateDistance'
import useCenter from '@/lib/useMapCenter'
import type { Coordinates, ExtendedPlace, ExtendedPlaces } from '@/types'

const props = defineProps<{
  mapRef: google.maps.Map | undefined
  places: ExtendedPlaces
}>()

const numberStores = config.settings.amountInList

const { center } = useCenter()

const kidsStore = ref(fondOfKidsStore)
const isCologne = ref<boolean | undefined>(false)
const insideStores = ref<ExtendedPlaces>([])
const outsideStores = ref<ExtendedPlaces>([])

const isInViewport = (coordinates: Coordinates): boolean =>
  !!props.mapRef?.getBounds()?.contains(coordinates)

const makeId = () => Math.random().toString(32).slice(2)

const isKidsStore = (place: ExtendedPlace) =>
  place.properties.name.toLowerCase().includes('fond of kids store')

const filterStores = () => {
  let inside: ExtendedPlaces = []
  let outside: ExtendedPlaces = []

  props.places.map((place) => {
    if (inside.length >= numberStores) return
    const { coordinates } = place.geometry
    const id = makeId()
    const isInside = isInViewport(coordinates)

    const properties = { ...place.properties, id }

    isInside
      ? inside.push({ ...place, properties })
      : outside.push({ ...place, properties })
  })

  if (inside.length < numberStores) {
    const difference = numberStores - inside.length
    outside = outside.slice(0, difference)
  } else {
    inside = inside.slice(0, numberStores)

    outside = []
  }

  return { inside, outside }
}

const runStoreFilter = (): void => {
  const { inside, outside } = filterStores()

  insideStores.value = inside
  outsideStores.value = outside

  const leftMenu = document.getElementById('leftMenu')
  if (leftMenu !== null) {
    leftMenu.scrollTop = 0
  }
}

const emit = defineEmits<(e: 'setStore', store: ExtendedPlace) => void>()

onMounted(() => {
  runStoreFilter()
  props.mapRef?.addListener('idle', () => runStoreFilter())
})

// refactor this to take place in the ListItem element instead of extending the object
// should be done for every other store aswell
watchEffect(
  () =>
    (kidsStore.value.geometry.distance = calculateDistance(
      center.value,
      fondOfKidsStore.geometry.coordinates,
    )),
)

watch(
  () => props.places,
  () => runStoreFilter(),
)

const geocoder = new google.maps.Geocoder()
watch(center, async () => {
  const city = 'Cologne, Germany'
  geocoder.geocode({ address: city }, (results, status) => {
    if (status === 'OK' && results) {
      const cityBounds = results[0].geometry.bounds
      isCologne.value = cityBounds?.contains(center.value)
    }
  })
})

const kidsStoreWithEvents = props.places.filter((place) =>
  place.properties.name.toLowerCase().includes('fond of kids store'),
)

if (kidsStoreWithEvents.length >= 1) {
  const [store] = kidsStoreWithEvents
  kidsStore.value = store
}

const filteredInsideStores = computed(() =>
  isCologne.value
    ? insideStores.value.filter((place) => !isKidsStore(place))
    : insideStores.value,
)
</script>

<template>
  <div
    v-if="insideStores.length + outsideStores.length > 0"
    class="flex flex-col gap-2 pb-4 pt-2"
  >
    <ListItem
      v-if="isCologne"
      :place="kidsStore"
      @click="() => emit('setStore', kidsStore)"
    />
    <ListItem
      v-for="place in filteredInsideStores"
      :key="place.properties.id"
      :place="place"
      @click="() => emit('setStore', place)"
    />
    <div
      v-if="outsideStores.length > 0"
      class="bg-primary-light py-2.5 text-center font-sans text-black"
    >
      <p>{{ $t("outside") }}</p>
    </div>
    <ListItem
      v-for="place in outsideStores"
      :key="place.properties.id"
      :place="place"
      @click="() => emit('setStore', place)"
    />
    <BtnImprint />
  </div>
  <div v-else class="px-2.5 py-6">
    <p class="text-center font-medium text-black">
      {{ $t("upsSorry") }}
    </p>
    <p class="text-grayt-medium px-10 text-center text-sm">
      {{ $t("noShopsMsg") }}
    </p>
  </div>
</template>
